import * as React from "react";
import { AlertTriangle, CheckCircle2, Loader2, Sparkles, X } from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import {
  Badge,
  Button,
  Checkbox,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  cn,
} from "@eduviaos/ui-kit";
import { PERMISSION_GROUPS, type PermissionId, type PermissionGroup } from "@eduviaos/shared";

/**
 * Phase H3 (2026-05-10) — reusable AI-suggest drawer used by:
 *   1. ``FirstRunInviteStaffPage`` — per-row "Suggest permissions"
 *      flow, since the H3 ``staff`` role ships with zero defaults and
 *      admins need an explicit way to discover what to grant.
 *   2. ``DesignationEditorSheet`` — replaces the old auto-merge
 *      pattern (the AI's filtered overrides used to silently extend
 *      the granted set) with the same checkbox + apply / dismiss UX
 *      so the admin always sees what's about to be applied before it
 *      lands on the editor canvas.
 *
 * The drawer is **stateless on the canvas**: it renders the AI's
 * suggested overrides as a checkbox list (all pre-checked), supports
 * bulk select-all / dismiss-all, lets the admin uncheck individual
 * rows, and finally emits the curated set via ``onApply`` when the
 * user commits. The parent owns the actual ``permission_overrides``
 * state — this drawer is purely a picker.
 *
 * UX contract per the user's H3 spec:
 *
 *   > "...there should also be a apply / reject button with
 *   > suggestions with checkbox for bulk apply / reject or
 *   > individual apply reject. and on apply it should auto tick
 *   > those roles / permissions..."
 *
 * Render states (mirrors the existing F2 `SuggestionBannerBody`
 * graceful-degradation matrix so the IA learns ONE pattern):
 *
 *   1. ``loading``        — async fetch in flight (skeleton + spinner)
 *   2. ``error``          — AI provider rejected / timed-out (banner + retry)
 *   3. ``parse_error``    — model returned non-JSON (banner + manual hint)
 *   4. ``empty``          — model returned []; "no overrides recommended"
 *   5. ``populated``      — N suggestions; checkbox list + bulk + apply
 *
 * Accessibility:
 *   - ``Sheet`` portal is focus-trapped by Radix; we anchor focus on
 *     the first checkbox after the loading spinner resolves.
 *   - ``aria-live="polite"`` on the status summary so screen readers
 *     hear the count change as the user toggles checkboxes.
 *   - ``Checkbox`` rows are full-width clickable labels.
 *   - Honours ``prefers-reduced-motion`` per Iron Rule #6.
 */

interface SuggestionItem {
  id: string;
  /** Catalog label if present; falls back to a humanised id. */
  label: string;
  /** Catalog helper if present; AI rationale otherwise; null for both. */
  helper: string | null;
  /** Free-text explanation from the AI. May overlap with `helper`. */
  rationale: string | null;
  /**
   * True when this suggestion's id wasn't found in
   * ``PERMISSION_GROUPS`` — typically because the BE catalog drifted
   * ahead of the FE. We still render the row (so the admin can apply
   * it) but tag it as "unknown to FE" for transparency.
   */
  catalog_miss: boolean;
}

/**
 * Inputs the parent renders into the drawer. ``suggestions`` is the
 * AI's filtered list (already ceiling-validated server-side).
 *
 * ``preselected`` is optional — pre-check rows whose ids are present.
 * Defaults to "all on" because the user's spec says "on apply it
 * should auto tick those roles / permissions" — i.e., the AI's
 * recommendation is the default selection.
 */
export interface SuggestPermissionsDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Heading shown above the list — e.g. "Suggest permissions for Hostel Warden". */
  title: string;
  /** One-line subtitle below the title — e.g. "Based on the role and label you entered". */
  subtitle?: string;
  /**
   * Tagged loading state of the upstream mutation. ``"idle"`` is
   * unused inside the drawer itself but accepted for parent symmetry.
   */
  status: "idle" | "loading" | "success" | "error";
  /** AI-suggested permission ids (filtered to the role's ceiling server-side). */
  suggestions: ReadonlyArray<string>;
  /** Per-id rationale strings from ``response.rationales``. */
  rationales: Readonly<Record<string, string>>;
  /** Permissions the AI proposed but the BE dropped (out-of-ceiling). */
  ceilingTruncated?: ReadonlyArray<string>;
  /** Permissions the AI proposed but the BE dropped (catalog miss). */
  unknownDropped?: ReadonlyArray<string>;
  /**
   * Provider / latency / model — surfaced as a "powered by …" footer
   * for transparency. Hidden when ``hideProviderFooter`` is set.
   */
  provider?: string;
  model?: string;
  fallbackDepth?: number;
  parseError?: string | null;
  /** Pre-check these rows on first render. Defaults to all suggestions (auto-tick). */
  preselected?: ReadonlyArray<string>;
  /** Top-level error message from the upstream mutation. Renders banner + Retry. */
  errorMessage?: string | null;
  /** Called when the user clicks "Try again" inside the error banner. */
  onRetry?: () => void;
  /**
   * Called with the final curated set when the user clicks "Apply".
   * The parent is responsible for merging this into the canvas state
   * and showing any "Pre-filled N permissions" toast / banner.
   */
  onApply: (selected: string[]) => void;
}

export function SuggestPermissionsDrawer({
  open,
  onOpenChange,
  title,
  subtitle,
  status,
  suggestions,
  rationales,
  ceilingTruncated = [],
  unknownDropped = [],
  provider,
  model,
  fallbackDepth = 0,
  parseError = null,
  preselected,
  errorMessage = null,
  onRetry,
  onApply,
}: SuggestPermissionsDrawerProps) {
  const reduceMotion = useReducedMotion();

  // Build the displayable rows from the raw suggestion ids — looking
  // up labels / helpers from the catalog so the drawer reads the same
  // way as the editor sheets it neighbours.
  const items = React.useMemo<SuggestionItem[]>(() => {
    const seen = new Set<string>();
    const out: SuggestionItem[] = [];
    for (const id of suggestions) {
      if (seen.has(id)) continue;
      seen.add(id);
      const catalogEntry = findCatalogEntry(id);
      const rationale = rationales[id]?.trim() || null;
      out.push({
        id,
        label: catalogEntry?.label ?? humaniseId(id),
        helper: catalogEntry?.helper ?? null,
        rationale,
        catalog_miss: !catalogEntry,
      });
    }
    return out;
  }, [suggestions, rationales]);

  // Selection set lives inside the drawer so the parent canvas only
  // sees the curated result on `onApply`. We pre-check whatever the
  // parent passed in `preselected` (defaults to ALL suggestions per
  // the user's "auto tick" spec).
  const initialSelected = React.useMemo(() => {
    const fallback = items.map((it) => it.id);
    const seed = preselected !== undefined ? Array.from(preselected) : fallback;
    return new Set(seed);
  }, [items, preselected]);

  const [selected, setSelected] = React.useState<Set<string>>(initialSelected);

  // Re-seed whenever the drawer (re)opens or the suggestion list changes —
  // closing then reopening the drawer for a different staff row should
  // not surface yesterday's selection.
  React.useEffect(() => {
    if (open) {
      setSelected(initialSelected);
    }
  }, [open, initialSelected]);

  const toggle = (id: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelected(new Set(items.map((it) => it.id)));
  };

  const clearAll = () => {
    setSelected(new Set());
  };

  const allSelected = items.length > 0 && selected.size === items.length;
  const noneSelected = selected.size === 0;
  const droppedCount = ceilingTruncated.length + unknownDropped.length;

  const handleApply = () => {
    // Preserve the AI's natural ordering by filtering through `items`
    // rather than the unordered `selected` set. Stable order →
    // deterministic UI when the parent re-renders the chip count.
    const out = items.filter((it) => selected.has(it.id)).map((it) => it.id);
    onApply(out);
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-lg"
        data-testid="suggest-permissions-drawer"
      >
        <SheetHeader
          className={cn(
            "border-border/60 border-b p-6",
            // Subtle violet wash so the AI surface reads as distinct
            // from the standard editor sheets it neighbours.
            "bg-gradient-to-br from-violet-50/40 to-transparent",
            "dark:from-violet-950/20",
          )}
        >
          <SheetTitle className="flex items-center gap-2">
            <span
              className={cn(
                "inline-flex size-7 shrink-0 items-center justify-center rounded-md",
                "bg-violet-100 text-violet-700",
                "dark:bg-violet-900/40 dark:text-violet-300",
              )}
              aria-hidden="true"
            >
              <Sparkles className="size-4" />
            </span>
            {title}
          </SheetTitle>
          <SheetDescription>
            {subtitle ??
              "AI suggestions based on the role and label you entered. Uncheck anything you don't want; click Apply when you're happy."}
          </SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto p-6">
          {status === "loading" ? (
            <LoadingState reduceMotion={!!reduceMotion} />
          ) : status === "error" || errorMessage ? (
            <ErrorBanner
              message={errorMessage ?? "Could not get an AI suggestion."}
              onRetry={onRetry}
            />
          ) : parseError ? (
            <ParseErrorBanner detail={parseError} />
          ) : items.length === 0 ? (
            <EmptyBanner droppedCount={droppedCount} />
          ) : (
            <PopulatedList
              items={items}
              selected={selected}
              onToggle={toggle}
              droppedCount={droppedCount}
            />
          )}

          {provider && (status === "success" || items.length > 0) ? (
            <ProviderFooter provider={provider} model={model} fallbackDepth={fallbackDepth} />
          ) : null}
        </div>

        {/* Footer toolbar — bulk + apply + cancel. Hidden during the
            loading state to avoid the user clicking Apply before the
            list renders. */}
        {status !== "loading" && items.length > 0 ? (
          <div className="border-border/60 flex flex-col gap-3 border-t p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-1.5">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={selectAll}
                  disabled={allSelected}
                  data-testid="suggest-permissions-select-all"
                >
                  <CheckCircle2 className="size-3.5" aria-hidden="true" />
                  Select all
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={clearAll}
                  disabled={noneSelected}
                  data-testid="suggest-permissions-clear-all"
                >
                  <X className="size-3.5" aria-hidden="true" />
                  Clear
                </Button>
              </div>
              <p className="text-muted-foreground text-xs" aria-live="polite" aria-atomic="true">
                {selected.size} of {items.length} selected
              </p>
            </div>
            <div className="flex items-center justify-end gap-2">
              <SheetClose asChild>
                <Button variant="outline" size="sm" type="button">
                  Cancel
                </Button>
              </SheetClose>
              <Button
                type="button"
                size="sm"
                onClick={handleApply}
                disabled={noneSelected}
                data-testid="suggest-permissions-apply"
              >
                <CheckCircle2 className="size-4" aria-hidden="true" />
                Apply {selected.size === 0 ? "" : `${selected.size} `}selection
                {selected.size === 1 ? "" : "s"}
              </Button>
            </div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

// ── Render helpers ────────────────────────────────────────────────────────

function LoadingState({ reduceMotion }: { reduceMotion: boolean }) {
  return (
    <div className="grid place-items-center gap-3 py-12">
      <motion.span
        animate={reduceMotion ? undefined : { rotate: 360 }}
        transition={reduceMotion ? undefined : { duration: 1.2, repeat: Infinity, ease: "linear" }}
        className="text-muted-foreground"
        aria-hidden="true"
      >
        <Loader2 className="size-6" />
      </motion.span>
      <p className="text-muted-foreground text-sm">Asking the AI for suggestions…</p>
    </div>
  );
}

function ErrorBanner({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: (() => void) | undefined;
}) {
  return (
    <div
      role="alert"
      className={cn(
        "rounded-lg border px-3 py-3 text-sm",
        "border-destructive/50 bg-destructive/5 text-destructive",
      )}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="font-medium">{message}</p>
          <p className="text-destructive/80 mt-0.5 text-xs">
            You can still grant permissions manually below — or retry the AI in a moment.
          </p>
        </div>
      </div>
      {onRetry ? (
        <div className="mt-3 flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={onRetry}>
            Try again
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function ParseErrorBanner({ detail }: { detail: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "rounded-lg border px-3 py-3 text-sm",
        "border-amber-300/60 bg-amber-50/60 text-amber-900",
        "dark:border-amber-500/40 dark:bg-amber-950/20 dark:text-amber-200",
      )}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <p className="font-medium">AI couldn't structure a suggestion this time.</p>
          <p className="mt-0.5 text-xs text-amber-900/80 dark:text-amber-200/80">{detail}</p>
        </div>
      </div>
    </div>
  );
}

function EmptyBanner({ droppedCount }: { droppedCount: number }) {
  return (
    <div
      className={cn(
        "rounded-lg border border-dashed px-3 py-6 text-center text-sm",
        "border-border/60 text-muted-foreground",
      )}
    >
      <p>The AI didn't recommend any specific permissions for this designation.</p>
      <p className="mt-1 text-xs">You can grant permissions manually after closing this drawer.</p>
      {droppedCount > 0 ? (
        <p className="text-muted-foreground/70 mt-3 text-[11px]">
          Dropped by policy: {droppedCount} permission{droppedCount === 1 ? "" : "s"} the AI
          proposed but the role's ceiling didn't allow.
        </p>
      ) : null}
    </div>
  );
}

function PopulatedList({
  items,
  selected,
  onToggle,
  droppedCount,
}: {
  items: SuggestionItem[];
  selected: Set<string>;
  onToggle: (id: string, checked: boolean) => void;
  droppedCount: number;
}) {
  return (
    <div className="space-y-2">
      <AnimatePresence initial={false}>
        {items.map((item) => {
          const isChecked = selected.has(item.id);
          return (
            <motion.label
              key={item.id}
              htmlFor={`suggest-${item.id}`}
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.16, ease: "easeOut" }}
              className={cn(
                "flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors",
                isChecked ? "border-primary/40 bg-primary/5" : "border-border/60 hover:bg-muted/30",
              )}
            >
              <Checkbox
                id={`suggest-${item.id}`}
                checked={isChecked}
                onCheckedChange={(c) => onToggle(item.id, c === true)}
                className="mt-0.5"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-foreground text-sm font-medium">{item.label}</p>
                  {item.catalog_miss ? (
                    <Badge variant="outline" className="text-[10px]">
                      New permission
                    </Badge>
                  ) : null}
                </div>
                {item.helper ? (
                  <p className="text-muted-foreground mt-0.5 text-xs">{item.helper}</p>
                ) : null}
                {item.rationale && item.rationale !== item.helper ? (
                  <p className="mt-1.5 text-[11px] italic text-violet-700 dark:text-violet-300">
                    AI: {item.rationale}
                  </p>
                ) : null}
              </div>
            </motion.label>
          );
        })}
      </AnimatePresence>
      {droppedCount > 0 ? (
        <p className="text-muted-foreground/70 mt-3 text-[11px]">
          Dropped by policy: {droppedCount} permission{droppedCount === 1 ? "" : "s"} the AI
          proposed but the role's ceiling didn't allow.
        </p>
      ) : null}
    </div>
  );
}

function ProviderFooter({
  provider,
  model,
  fallbackDepth,
}: {
  provider: string;
  model?: string;
  fallbackDepth: number;
}) {
  return (
    <p className="text-muted-foreground/70 mt-4 text-[11px]">
      {fallbackDepth > 0 ? (
        <>
          Powered by <span className="font-medium">{provider}</span> (fallback)
          {model ? ` · ${model}` : ""}
        </>
      ) : (
        <>
          Powered by <span className="font-medium">{provider}</span>
          {model ? ` · ${model}` : ""}
        </>
      )}
    </p>
  );
}

// ── Catalog lookup helpers ────────────────────────────────────────────────

interface CatalogEntry {
  id: PermissionId;
  label: string;
  helper: string;
}

const CATALOG_INDEX: ReadonlyMap<string, CatalogEntry> = (() => {
  const map = new Map<string, CatalogEntry>();
  for (const group of PERMISSION_GROUPS as ReadonlyArray<PermissionGroup>) {
    for (const perm of group.permissions) {
      map.set(perm.id, perm);
    }
  }
  return map;
})();

function findCatalogEntry(id: string): CatalogEntry | undefined {
  return CATALOG_INDEX.get(id);
}

/**
 * Convert a snake_case / colon-separated permission id (e.g.
 * ``"manage_admissions"``, ``"admissions:override_stage_gate"``) into
 * a humanised label as a last-resort fallback when the FE catalog
 * doesn't carry the entry yet. Keep this purely deterministic — never
 * call into the AI rationale text here.
 */
function humaniseId(id: string): string {
  const cleaned = id.replace(/[:_]/g, " ");
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}
